import React, { useState, useEffect } from "react";
import cn from "classnames";
import { useRouter } from "next/router";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { friendlyFormatIBAN } from "ibantools";

import CustomFormatNumber from "components/CustomFormatNumber";
import SideModal from "components/SideModal";
import InvestmentPictureOverview from "components/InvestmentPictureOverview";
import PrevButton from "components/PrevButton";
import FormatDateWithLocale from "components/FormatDateWithLocale";
import ExpandableTab from "components/ExpandableTab";
import DownloadButton from "components/DownloadButton";
import OrderItem from "components/OrderItem";

import Check2 from "assets/images/svg/check2.svg";
import EWallet from "assets/images/svg/e-wallet-gray.svg";
import IconInfo from "assets/images/svg/icon-info.svg";

import { INVESTMENT_STATUS, KYC_STATUS_LOCAL } from "constants/status";
import { useFormattedMessage } from "hooks";
import { addOverlay, sideModalScrollHidden, copyToClipboard } from "utils";
import { sendFormError } from "i18n/messages/errors";
import { ACCOUNT_HASH_VARIABLES } from "constants/index";
import { primaryMarketInvestmentTan, settlePrimaryMarketInvestment, getProduct } from "api";

import { selectMangopayActive, selectPayInWalletBalance, selectUserId } from "store/user/account/selectors";
import { selectProjectDetails } from "store/project/selectors";

import IntlFormattedMessage from "./IntlFormattedMessage";
import { getDocumentsHost } from "common/helper";
import { fetchDashboardDetailsStart } from "store/user/dashboard/actions";
import { fetchPayInWalletBalanceSuccess, fetchUserPaymentsStart } from "store/user/account/actions";
import OTPInput from "./OTPInput";
import NewLoader from "./NewLoader";
import { selectCurrenKYCStatus, selectKYCCompleted, selectKYCServerError } from "store/user/kyc/selectors";

const DOCUMENT_HOST = getDocumentsHost();

const messages = {
    sendFormError,
    investmentHeading: "continue_investment_heading",
    heading: "what_next",
    stepFirstText: "investment_status_modal_step_first_text",
    stepSecondOneText: "investment_status_modal_step_second_one_text",
    stepSecondTwoText: "identification_in_check",
    stepSecondThreeText: "identification_required",
    stepSecondContentText: "investment_status_modal_step_second_content",
    stepSecondButtonText: "kyc_start_identification",
    stepThirdOneText: "offer_accepted",
    stepThirdTwoText: "investment_offer_under_review",
    stepThirdContentText: "your_investment_offer_being_checked",
    stepFourthText: "payment_transfer_due",
    stepFourthContentText: "transfer_investment_amount",
    stepFourthContentHeadingOneText: "recipient_of_payment",
    stepFourthContentButtonText: "purpose_copy",
    stepFourthContentButtonTextCopied: "copied",
    stepFifthText: "investment_successful",
    stepSixthText: "financing_goal_achieved",
    stepSixthTextContent: "investment_incomplete",
    stepSeventhText: "issue_of_tokens",
    stepEigthText: "investment_completed",
    stepPayedOutText: "successfully_payed_out",
    completedInvestment: "completed_investment",
    canceledText: "investment_offer_declined",
    completedPurchase: "completed_purchase",
    completedSell: "completed_sell",
    successfullySold: "success_fully_sold",
    successfullyBought: "successfully_bought",
    successfullyCompleted: "investment_status_successful",
    documentsTitle: "meta_title_admin_documents_page",
    viewAllDocuments: "view_all_documents",
    yourTotalAmount: "your_total_amount",
    recipient: "recipient",
    investedAmount: "invested_amount",
    subscriptionAmount: "invested_amount",
    backToProductPage: "back_to_product_page",
    purpose: "investment_purpose",
    payNowWithCredit: "pay_now_with_credit",
    technicalProblemContent1: "technical_problem_content1",
    technicalProblemContent2: "technical_problem_content2",
    paymentDetails: "investment_payment_details",
    paymentSuccessful: "investment_payment_successful",
    amountTransferred: "investment_amount_transferred",
    notActivatedContent1: "not_activated_content1",
    notActivatedContent2: "not_activated_content2",
    activateEwalletNow: "activate_ewallet_now",
    topUpContent: "top_up_content",
    topUpCredit: "top_up_credit",
    OTPTitle: "payment_confirmation",
    OTPContent: "otp_code",
    OTPButtonText: "confirmation",
    notAvailable: "not_available",
    transferText: "bank_transfer",
    ewalletText: "ewallet_credit",
    boEditOTPResendText: "resend_otp_code",
    invalidOTP: "invalid_otp",
    invalidTan: "invalid_tan",
    mobileTANcodeExpired: "mobile_tan_code_expired",
    sendCodeAgainIn: "send_code_again",
    kycServerError: "kyc_server_error",
};

const errorsMessagesMapping = {
    invalid_input: <IntlFormattedMessage id={messages.invalidOTP} />,
    invalid_TAN: <IntlFormattedMessage id={messages.invalidTan} />,
    TAN_expired: <IntlFormattedMessage id={messages.mobileTANcodeExpired} />,
};

const statusStep = {
    [INVESTMENT_STATUS.INITIALIZED]: 1,
    [INVESTMENT_STATUS.CREATED_PENDING_KYC]: 2,
    [INVESTMENT_STATUS.CREATED_PENDING_LIABILITIY_UMBRELLA_CLEARANCE]: 3,
    [INVESTMENT_STATUS.CREATED_PENDING_ISSUER_CLEARANCE]: 3,
    [INVESTMENT_STATUS.CREATED_PENDING_PAYMENT]: 4,
    [INVESTMENT_STATUS.CREATED_PENDING_PROJECT_FINANCING]: 6,
    [INVESTMENT_STATUS.CREATED_PENDING_PROJECT_GO_LIVE]: 7,
    [INVESTMENT_STATUS.ACTIVE]: 8,
    [INVESTMENT_STATUS.INVESTMENT_SUCCESSFULLY_PAYED_OUT]: 9,
    [INVESTMENT_STATUS.SECONDARY_MARKET_BUY]: 10,
    [INVESTMENT_STATUS.SECONDARY_MARKET_SALE]: 11,
};

const stepIcon = {
    success: "fas fa-check-circle",
    pending: "fas fa-hourglass-half",
    canceled: "fas fa-times-circle",
};

const BANK_TRANSFER = "bankTransfer";
const EWALLET = "ewallet";
const ENOUGH_BALANCE = "enoughBalance";
const EWALLET_OTP = "eWalletOTP";
const TECHNICAL_PROBLEM = "technicalProblem";
const PAYMENT_SUCCESSFULL = "paymentSuccessful";
const NOT_ACTIVATED = "notActivated";
const TOP_UP_EWALLET = "topUpEwallet";

const InvestmentStep = ({ className, title, icon }) => (
    <div className={`investment-status border-radius ${className}`}>
        <div className="flex justify-between investment-status-header">
            <h5 className="fw-400 m-0">{title}</h5>
            {stepIcon[icon] && (
                <span className="investment-status-icon">
                    <i className={stepIcon[icon]} />
                </span>
            )}
        </div>
    </div>
);

const CompleteInvestment = ({ heading, statusTitle, documentsHeading, documents = [] }) => {
    const { formatMessage } = useFormattedMessage();

    const isDocumentExists = (documentTitle) => {
        return documents && documents.find((document) => document.Title === documentTitle);
    };

    return (
        <div>
            <h4>{heading}</h4>
            <InvestmentStep className="completed mb-10" title={statusTitle} icon="success" />
            <div className="border-x" />
            <h4 className="mb-6">{documentsHeading}</h4>
            <ExpandableTab title={formatMessage(messages.viewAllDocuments)}>
                <div className="px-4 py-8">
                    {Array.isArray(documents) &&
                        documents.map(({ Title, Filename, Path }, index) => {
                            // if (useTitleMapper && typeof titleMapper[Title] === "undefined") return null
                            const file = Title && isDocumentExists(Title);
                            return (
                                file && (
                                    <DownloadButton
                                        key={index}
                                        isToken
                                        title={Title}
                                        fileURL={`${DOCUMENT_HOST}/download${Path}${Filename}`}
                                    />
                                )
                            );
                        })}
                </div>
            </ExpandableTab>
        </div>
    );
};

export const RenderInvestmentStatus = ({
    open,
    investmentData,
    currentKYCStatus,
    hideInvestmentStatusModal,
    hideInvestmentModal,
    setKycModalFlag,
    setInvestmentKycModalFlag,
    setAdequacyStartStep,
    clearInvestmentStepData,
    isKYCCompleted,
    project,
    isMangopayActive,
    payInWalletBalance,
    onCloseButtonClick,
    fetchPayInWalletBalanceSuccess,
    fetchDashboardDetailsStart,
    fetchUserPaymentsStart,
    userId,
    isKYCServerError,
}) => {
    const router = useRouter();
    const { formatMessage } = useFormattedMessage();
    const [selectedPaymentMethod, setSelectedPaymentmethod] = useState(BANK_TRANSFER);
    const [isSingleProductLoading, setSingleProductLoading] = useState(false);
    const [isIssuerFundingWalletAvailable, setIssuerFundingWalletAvailable] = useState(false);
    const [ewalletCurrentStep, setEwalletCurrentStep] = useState(NOT_ACTIVATED);
    const [OTP, setOTP] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [isTanLoading, setTanLoading] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [otpTimeCounter, setOtpTimeCounter] = useState(0);

    const showKycButton = currentKYCStatus === KYC_STATUS_LOCAL.INITIATED;

    const kycPending = currentKYCStatus === KYC_STATUS_LOCAL.PENDING;

    const bankData = project?.financials?.bankAccount;
    const accountOwner = bankData?.accountOwner;
    const IBAN = bankData?.IBAN || bankData?.iban;
    const BIC = bankData?.BIC || bankData?.bic;
    const user = investmentData?.User;
    const firstName = user?.FirstName;
    const lastName = user?.LastName;
    const ReferenceNumber = investmentData?.ReferenceNumber;
    const amount = investmentData?.Amount;
    const status = investmentData?.StatusInvestment;

    const resetSteps = () => {
        setSelectedPaymentmethod(BANK_TRANSFER);
        setIssuerFundingWalletAvailable(false);
        setEwalletCurrentStep(NOT_ACTIVATED);
        setOTP("");
    };

    const getSingleProduct = (projectID) => {
        try {
            setSingleProductLoading(true);

            getProduct(projectID)
                .then((res) => {
                    res && setSingleProductLoading(false);
                    if (res.data.errors) {
                        const error = res.data.errors[0].message;
                        if (errorsMessagesMapping[error]) {
                            setErrorMessage(errorsMessagesMapping[error]);
                        } else {
                            setErrorMessage(formatMessage(messages.sendFormError));
                        }
                        return;
                    }

                    if (res?.data?.data?.product?.product?.IsAvailableForEwalletFunding === "true") {
                        setIssuerFundingWalletAvailable(true);
                        setNecessarySteps();
                    }

                    setErrorMessage("");
                })
                .catch(() => {
                    setErrorMessage(formatMessage(messages.sendFormError));
                    setSingleProductLoading(false);
                });
        } catch (error) {
            setErrorMessage(formatMessage(messages.sendFormError));
            setSingleProductLoading(false);
        }
    };

    // To set the latest step
    useEffect(() => {
        investmentData?.StatusInvestment && setStep(statusStep[investmentData?.StatusInvestment]);
    }, [investmentData?.StatusInvestment]);

    useEffect(() => {
        if (!open) {
            if (ewalletCurrentStep === PAYMENT_SUCCESSFULL) {
                fetchDashboardDetailsStart();
                userId && fetchUserPaymentsStart(userId);
            }
            resetSteps();
        }
    }, [open]);

    useEffect(() => {
        open && project?.projectID && step === 4 && getSingleProduct(project.projectID);
    }, [open, project?.projectID, step]);

    useEffect(() => {
        if (errorCode === "invalid_input") {
            setTimeout(() => {
                setOTP("");
                setErrorCode("");
            }, 500);
        }
    }, [errorCode]);

    useEffect(() => {
        OTP.length === 0 && setErrorMessage("");
    }, [OTP]);

    useEffect(() => {
        if (otpTimeCounter > 0 && !isTanLoading) {
            const interval = setInterval(() => {
                setOtpTimeCounter(otpTimeCounter - 1);
            }, 1000);

            if (!open) {
                clearInterval(interval);
                setOtpTimeCounter(0);
            }
            return () => clearInterval(interval);
        }
    }, [isTanLoading, otpTimeCounter, open]);

    const setNecessarySteps = () => {
        setSelectedPaymentmethod(EWALLET);
        isMangopayActive
            ? parseFloat(payInWalletBalance) >= parseFloat(amount)
                ? setEwalletCurrentStep(ENOUGH_BALANCE)
                : setEwalletCurrentStep(TOP_UP_EWALLET)
            : setEwalletCurrentStep(NOT_ACTIVATED);
    };

    const getTAN = () => {
        try {
            setOTP("");
            setTanLoading(true);
            primaryMarketInvestmentTan(investmentData?.uid)
                .then((res) => {
                    res && setTanLoading(false);
                    if (res.data.errors) {
                        const error = res.data.errors[0].message;
                        if (errorsMessagesMapping[error]) {
                            setErrorMessage(errorsMessagesMapping[error]);
                        } else {
                            setErrorMessage(formatMessage(messages.sendFormError));
                        }
                        return;
                    }

                    setErrorMessage("");
                    setEwalletCurrentStep(EWALLET_OTP);
                })
                .catch(() => {
                    setErrorMessage(formatMessage(messages.sendFormError));
                    setTanLoading(false);
                });
        } catch (error) {
            setErrorMessage(formatMessage(messages.sendFormError));
            setTanLoading(false);
        }
    };

    const onSettlement = () => {
        try {
            setSubmitting(true);

            const variables = {
                investmentID: investmentData?.uid,
                Tan: OTP,
            };
            settlePrimaryMarketInvestment(variables)
                .then((res) => {
                    res && setSubmitting(false);

                    if (res.data.errors) {
                        const error = res.data.errors[0].message;
                        setErrorCode(error);
                        if (errorsMessagesMapping[error]) {
                            setErrorMessage(errorsMessagesMapping[error]);
                        } else {
                            // setErrorMessage(formatMessage(messages.sendFormError));
                            setEwalletCurrentStep(TECHNICAL_PROBLEM);
                        }
                        return;
                    }

                    setStep(5); // To show the payment successfull in Investment Successful tab
                    // setEwalletCurrentStep(PAYMENT_SUCCESSFULL);

                    // Update payIn wallet balance
                    fetchPayInWalletBalanceSuccess({
                        Amount: parseFloat(payInWalletBalance) - parseFloat(amount),
                    });
                })
                .catch(() => {
                    setErrorMessage(formatMessage(messages.sendFormError));
                    setSubmitting(false);
                });
        } catch (error) {
            setErrorMessage(formatMessage(messages.sendFormError));
            setSubmitting(false);
        }
    };

    function resendTAN() {
        getTAN();
        setOtpTimeCounter(90);
    }

    const detailsOnEwalletSelection = {
        [ENOUGH_BALANCE]: (
            <>
                <div>
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.recipient)}:</span>
                    <span className="text-14 d-block lighter-grey-color">{accountOwner}</span>
                </div>
                <div className="mt-6">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.subscriptionAmount)}:</span>
                    <span className="text-14 d-block lighter-grey-color">
                        <CustomFormatNumber value={amount} />
                    </span>
                </div>
                {!isTanLoading && !isSubmitting && errorMessage && (
                    <span className="text-12 alert-red-color d-block mt-3">{errorMessage}</span>
                )}
                <button
                    disabled={isTanLoading}
                    className={cn({
                        "mw-100 mt-8": true,
                        "disable-grey": isTanLoading,
                    })}
                    onClick={getTAN}
                >
                    {isTanLoading ? (
                        <>
                            <NewLoader /> {formatMessage(messages.payNowWithCredit)}
                        </>
                    ) : (
                        formatMessage(messages.payNowWithCredit)
                    )}
                </button>
            </>
        ),
        [EWALLET_OTP]: (
            <>
                <h5 className="mt-0">{formatMessage(messages.OTPTitle)}</h5>
                <span className="d-block text-14 mb-6">{formatMessage(messages.OTPContent)}</span>
                <OTPInput
                    errorCode={errorCode}
                    value={OTP}
                    onChange={(value) => setOTP(value)}
                    isSubmitting={isSubmitting}
                    errorMessage={errorMessage}
                    shouldAutoFocus
                />
                <div className="flex justify-end">
                    {isTanLoading ? (
                        <NewLoader className="mt-2" />
                    ) : otpTimeCounter ? (
                        <span className="text-12 mt-2 cursor-pointer">
                            {`${formatMessage(messages.sendCodeAgainIn)} ${otpTimeCounter} sec`}
                        </span>
                    ) : (
                        <span className="text-12 mt-2 cursor-pointer" onClick={() => resendTAN()}>
                            {formatMessage(messages.boEditOTPResendText)}
                        </span>
                    )}
                </div>
                {!isTanLoading && !isSubmitting && errorMessage && (
                    <span className="text-12 alert-red-color d-block mt-3">{errorMessage}</span>
                )}
                <button
                    disabled={isSubmitting || isTanLoading}
                    className={cn({
                        "mw-100": true,
                        "disable-grey": isSubmitting || isTanLoading || OTP?.length !== 6,
                    })}
                    onClick={onSettlement}
                >
                    {isSubmitting ? (
                        <>
                            <NewLoader /> {formatMessage(messages.OTPButtonText)}
                        </>
                    ) : (
                        formatMessage(messages.OTPButtonText)
                    )}
                </button>
            </>
        ),
        [TECHNICAL_PROBLEM]: (
            <>
                <div className="bg-lighter-grey border-radius black-color p-4">
                    <i className="fas fa-hourglass-half mb-3" />
                    <span className="text-14 mb-1 d-block">
                        <IntlFormattedMessage id={messages.technicalProblemContent1} />
                    </span>
                    <span className="text-14 d-block">{formatMessage(messages.technicalProblemContent2)}</span>
                </div>
                <h5 className="mt-8">{formatMessage(messages.paymentDetails)}</h5>
                <div className="mt-5">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.recipient)}:</span>
                    <span className="text-14 d-block lighter-grey-color">{accountOwner}</span>
                </div>
                <div className="mt-6">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.subscriptionAmount)}:</span>
                    <span className="text-14 d-block lighter-grey-color">
                        <CustomFormatNumber value={amount} />
                    </span>
                </div>
                <button
                    className="mw-100 mt-8"
                    onClick={() => router.push(`/marketplace/${investmentData?.LinkProduct?.ProductID}`)}
                >
                    {formatMessage(messages.backToProductPage)}
                </button>
            </>
        ),
        [PAYMENT_SUCCESSFULL]: (
            <>
                <div className="bg-light-green border-radius black-color p-4">
                    <div className="d-flex">
                        <i className="fas fa-check-circle check-circle-color mb-3 mr-2" />
                        <span className="text-14 fw-500 mb-1 d-block">{formatMessage(messages.paymentSuccessful)}</span>
                    </div>
                    <span className="text-14 d-block">{formatMessage(messages.amountTransferred)}</span>
                </div>
                <h5 className="mt-8">{formatMessage(messages.paymentDetails)}</h5>
                <div className="mt-5">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.recipient)}:</span>
                    <span className="text-14 d-block lighter-grey-color">{accountOwner}</span>
                </div>
                <div className="mt-6">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.subscriptionAmount)}:</span>
                    <span className="text-14 d-block lighter-grey-color">
                        <CustomFormatNumber value={amount} />
                    </span>
                </div>
            </>
        ),
        [NOT_ACTIVATED]: (
            <>
                <div className="d-flex border-1 border-radius p-4 pr-6">
                    <IconInfo width="40px" height="16px" className="mr-2" />
                    <div>
                        <span className="text-14 d-block mb-1">{formatMessage(messages.notActivatedContent1)}</span>
                        <span className="text-14 d-block">{formatMessage(messages.notActivatedContent2)}</span>
                    </div>
                </div>
                <button
                    className="mw-100 mt-8"
                    onClick={() => {
                        window.location.hash = ACCOUNT_HASH_VARIABLES.ACCOUNT_SM_ONBOARDING;
                        onCloseButtonClick();
                        addOverlay();
                    }}
                >
                    {formatMessage(messages.activateEwalletNow)}
                </button>
            </>
        ),
        [TOP_UP_EWALLET]: (
            <>
                <div>
                    <span className="text-14 fw-600 lighter-grey-color mt-0">{formatMessage(messages.yourTotalAmount)}:</span>
                    <span className="text-14 d-block lighter-grey-color">{formatMessage(messages.topUpContent)}</span>
                </div>
                <div className="mt-6">
                    <span className="text-14 fw-600 lighter-grey-color">{formatMessage(messages.subscriptionAmount)}:</span>
                    <span className="text-14 d-block lighter-grey-color">
                        <CustomFormatNumber value={amount} />
                    </span>
                </div>
                <button
                    className="mw-100 mt-8"
                    onClick={() => {
                        window.location.hash = ACCOUNT_HASH_VARIABLES.ACCOUNT_EWALLET;
                        onCloseButtonClick();
                        addOverlay();
                    }}
                >
                    {formatMessage(messages.topUpCredit)}
                </button>
            </>
        ),
    };

    return (
        status && (
            <>
                <div className="investment-status-container">
                    {step === 8 || step === 10 || step === 11 ? (
                        <CompleteInvestment
                            heading={
                                step === 8
                                    ? formatMessage(messages.completedInvestment)
                                    : step === 10
                                      ? formatMessage(messages.completedPurchase)
                                      : formatMessage(messages.completedSell)
                            }
                            statusTitle={
                                step === 8
                                    ? formatMessage(messages.successfullyCompleted)
                                    : step === 10
                                      ? formatMessage(messages.successfullyBought)
                                      : formatMessage(messages.successfullySold)
                            }
                            documentsHeading={formatMessage(messages.documentsTitle)}
                            documents={investmentData?.Document}
                        />
                    ) : step === 9 ? (
                        <>
                            <h4>{formatMessage(messages.completedInvestment)}</h4>
                            <InvestmentStep
                                className="completed mb-10"
                                title={formatMessage(messages.stepPayedOutText)}
                                icon="success"
                            />
                        </>
                    ) : (
                        <>
                            <h4 className="text-left">{formatMessage(messages.heading)}</h4>
                            <div className="investment-status border-radius completed">
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">{formatMessage(messages.stepFirstText)}</h5>
                                    <span className="investment-status-icon">
                                        <i className="fas fa-check-circle" />
                                    </span>
                                </div>
                            </div>
                            <div
                                className={`investment-status border-radius ${
                                    step > 2 || (isKYCCompleted && step === 2) ? "completed" : "pending"
                                }`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">
                                        {step > 2
                                            ? formatMessage(messages.stepSecondOneText)
                                            : step === 1 || (step === 2 && (isKYCServerError || showKycButton))
                                              ? formatMessage(messages.stepSecondThreeText)
                                              : kycPending
                                                ? formatMessage(messages.stepSecondTwoText)
                                                : isKYCCompleted && step === 2
                                                  ? formatMessage(messages.stepSecondOneText)
                                                  : ""}
                                    </h5>
                                    <>
                                        {((isKYCCompleted && step === 2) || step > 2 || kycPending) && (
                                            <span className="investment-status-icon">
                                                <i className={`fas fa-${kycPending ? "hourglass-half" : "check-circle"}`} />
                                            </span>
                                        )}
                                    </>
                                </div>
                                <>
                                    {(step === 1 || (step === 2 && (isKYCServerError || showKycButton))) && (
                                        <div className="investment-status-content">
                                            <p>
                                                {isKYCServerError
                                                    ? formatMessage(messages.kycServerError)
                                                    : formatMessage(messages.stepSecondContentText)}
                                            </p>
                                            <button
                                                disabled={isKYCServerError}
                                                onClick={() => {
                                                    if (!isKYCServerError) {
                                                        setKycModalFlag && setKycModalFlag();
                                                        setInvestmentKycModalFlag && setInvestmentKycModalFlag();
                                                        clearInvestmentStepData && clearInvestmentStepData();
                                                        hideInvestmentStatusModal && hideInvestmentStatusModal();
                                                        hideInvestmentModal && hideInvestmentModal();
                                                        setAdequacyStartStep && setAdequacyStartStep({ step: 1 });
                                                        addOverlay();
                                                    }
                                                }}
                                            >
                                                {formatMessage(messages.stepSecondButtonText)}
                                            </button>
                                        </div>
                                    )}
                                </>
                            </div>
                            <div
                                className={`investment-status border-radius ${step >= 3 && (step > 3 ? "completed" : "pending")}`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">
                                        {step > 3
                                            ? formatMessage(messages.stepThirdOneText)
                                            : formatMessage(messages.stepThirdTwoText)}
                                    </h5>
                                    {step > 3 && (
                                        <span className="investment-status-icon">
                                            <i className="fas fa-check-circle" />
                                        </span>
                                    )}
                                </div>
                                {step === 3 && (
                                    <div className="investment-status-content">
                                        <p>{formatMessage(messages.stepThirdContentText)}</p>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`investment-status border-radius ${step >= 4 && (step > 4 ? "completed" : "pending")}`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">{formatMessage(messages.stepFourthText)}</h5>
                                    {step >= 4 && (
                                        <span className="investment-status-icon">
                                            <i className={`fas fa-${step === 4 ? "hourglass-half" : "check-circle"}`} />
                                        </span>
                                    )}
                                </div>
                                {step === 4 && (
                                    <div className="investment-status-content">
                                        {isSingleProductLoading ? (
                                            <NewLoader className="w-100 d-flex justify-center mb-4" />
                                        ) : (
                                            <>
                                                {(selectedPaymentMethod === BANK_TRANSFER ||
                                                    ewalletCurrentStep === ENOUGH_BALANCE ||
                                                    ewalletCurrentStep === NOT_ACTIVATED ||
                                                    ewalletCurrentStep === TOP_UP_EWALLET ||
                                                    ewalletCurrentStep === ENOUGH_BALANCE) && (
                                                    <>
                                                        <p>{formatMessage(messages.stepFourthContentText)}</p>
                                                        <div className="payment-method flex justify-between flex-wrap mt-6">
                                                            <div
                                                                className={`single-method ${
                                                                    selectedPaymentMethod === BANK_TRANSFER ? "active" : ""
                                                                }`}
                                                                onClick={() => setSelectedPaymentmethod(BANK_TRANSFER)}
                                                            >
                                                                <Check2 />
                                                                <span className="text-14 fw-600 d-block">
                                                                    {formatMessage(messages.transferText)}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={`single-method ${
                                                                    !isIssuerFundingWalletAvailable ? "deactive" : ""
                                                                } ${selectedPaymentMethod === EWALLET ? "active" : ""}`}
                                                                datatitle={`${
                                                                    !isIssuerFundingWalletAvailable
                                                                        ? formatMessage(messages.notAvailable)
                                                                        : ""
                                                                }`}
                                                                onClick={() =>
                                                                    isIssuerFundingWalletAvailable
                                                                        ? setSelectedPaymentmethod(EWALLET)
                                                                        : null
                                                                }
                                                            >
                                                                <EWallet />
                                                                <span className="text-14 fw-600 d-block">
                                                                    {formatMessage(messages.ewalletText)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="bank-details-block">
                                                    <div className="bank-details">
                                                        {selectedPaymentMethod === EWALLET ? (
                                                            detailsOnEwalletSelection[ewalletCurrentStep]
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <span className="text-14 fw-600 lighter-grey-color mt-0">
                                                                        {formatMessage(messages.stepFourthContentHeadingOneText)}
                                                                    </span>
                                                                    <span
                                                                        className="text-14 d-block lighter-grey-color mb-3 cursor-pointer"
                                                                        onClick={() => copyToClipboard(accountOwner)}
                                                                    >
                                                                        {accountOwner} <i className="far fa-copy ml-2" />
                                                                    </span>
                                                                    <span className="text-14 d-block lighter-grey-color mb-3 cursor-pointer">
                                                                        IBAN:{" "}
                                                                        <span
                                                                            className="font-noto-serif"
                                                                            onClick={() => copyToClipboard(IBAN)}
                                                                        >
                                                                            {friendlyFormatIBAN(IBAN)}{" "}
                                                                            <i className="far fa-copy ml-2" />
                                                                        </span>
                                                                    </span>
                                                                    <span className="text-14 d-block lighter-grey-color cursor-pointer">
                                                                        BIC:{" "}
                                                                        <span
                                                                            className="font-noto-serif"
                                                                            onClick={() => copyToClipboard(BIC)}
                                                                        >
                                                                            {BIC}
                                                                        </span>{" "}
                                                                        <i className="far fa-copy ml-2" />
                                                                    </span>
                                                                </div>
                                                                <div className="mt-6">
                                                                    <span className="text-14 fw-600 lighter-grey-color">
                                                                        {formatMessage(messages.subscriptionAmount)}:
                                                                    </span>
                                                                    <span
                                                                        className="text-14 d-block lighter-grey-color cursor-pointer"
                                                                        onClick={() => copyToClipboard(amount)}
                                                                    >
                                                                        <CustomFormatNumber value={amount} />{" "}
                                                                        <i className="far fa-copy ml-2" />
                                                                    </span>
                                                                </div>
                                                                <div className="mt-6">
                                                                    <span className="text-14 fw-600 lighter-grey-color">
                                                                        {formatMessage(messages.purpose)}:
                                                                    </span>
                                                                    <span
                                                                        className="text-14 d-block lighter-grey-color cursor-pointer"
                                                                        onClick={() =>
                                                                            copyToClipboard(
                                                                                `${firstName} - ${lastName} - ${ReferenceNumber}`,
                                                                            )
                                                                        }
                                                                    >
                                                                        {`${firstName} ${lastName} ${ReferenceNumber}`}{" "}
                                                                        <i className="far fa-copy ml-2" />
                                                                    </span>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div
                                className={`investment-status border-radius ${step >= 5 && (step > 5 ? "completed" : "pending")}`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">{formatMessage(messages.stepFifthText)}</h5>
                                    {step >= 5 && (
                                        <span className="investment-status-icon">
                                            <i className="fas fa-check-circle" />
                                        </span>
                                    )}
                                </div>
                                {step === 5 && (
                                    <div className="investment-status-content">
                                        <div className="bank-details-block">
                                            <div className="bank-details">{detailsOnEwalletSelection[PAYMENT_SUCCESSFULL]}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`investment-status border-radius ${step >= 6 && (step > 6 ? "completed" : "pending")}`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">{formatMessage(messages.stepSixthText)}</h5>
                                    {step >= 6 && (
                                        <span className="investment-status-icon">
                                            <i className={`fas fa-${step === 6 ? "hourglass-half" : "check-circle"}`} />
                                        </span>
                                    )}
                                </div>
                                {step === 6 && (
                                    <div className="investment-status-content">
                                        <p>{formatMessage(messages.stepSixthTextContent)}</p>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`investment-status border-radius ${step >= 7 && (step > 7 ? "completed" : "pending")}`}
                            >
                                <div className="flex justify-between investment-status-header">
                                    <h5 className="fw-400 m-0">{formatMessage(messages.stepSeventhText)}</h5>
                                    {step >= 7 && (
                                        <span className="investment-status-icon">
                                            <i className={`fas fa-${step === 7 ? "hourglass-half" : "check-circle"}`} />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </>
        )
    );
};

const InvestmentStatusModal = ({
    open,
    investmentData,
    userCountry,
    onCloseButtonClick,
    hideInvestmentStatusModal,
    setKycModalFlag,
    currentKYCStatus,
    onBackButtonClick,
    isKYCCompleted,
    allProjects,
    isMangopayActive,
    payInWalletBalance,
    fetchPayInWalletBalanceSuccess,
    fetchDashboardDetailsStart,
    fetchUserPaymentsStart,
    userId,
    isKYCServerError,
}) => {
    const { formatMessage } = useFormattedMessage();

    const singleProject = (id) => allProjects?.find((item) => item.projectID === id) || {};

    const project = singleProject(investmentData?.LinkProduct?.ProductID);
    const image = project && project.images && project.images.teaserImage ? `${project.images.teaserImage.path}?h=150` : null;
    const title = project && project.title;
    const location = `${project && project.address ? project.address.city : ""}${
        project && project.address ? `, ${project.address.country}` : ""
    }`;
    const date = investmentData?.CreatedDate;
    const splitDate = date && date.split("_");
    const newDate = splitDate && splitDate[0] && new Date(splitDate[0]);
    const createdDate = newDate && <FormatDateWithLocale date={newDate} />;
    const price = investmentData?.SharePrice;
    const shares = investmentData?.Shares;
    const amount = investmentData?.Amount;

    const step = investmentData?.StatusInvestment && statusStep[investmentData?.StatusInvestment];

    useEffect(() => {
        open && sideModalScrollHidden(open);
    }, [open]);

    return (
        <SideModal
            className="investment-status-modal"
            containerClassName="investment-status-container"
            heading={formatMessage(messages.investmentHeading)}
            open={open}
            onCloseButtonClick={() => {
                onCloseButtonClick();
            }}
        >
            {step === 10 || step === 11 ? (
                <OrderItem
                    className="tile-hover-effect mb-14"
                    tag={investmentData?.StatusInvestment}
                    image={project?.images?.teaserImage?.path}
                    title={project?.title}
                    city={project?.address?.city}
                    shares={investmentData?.Shares}
                    price={parseFloat(investmentData?.Amount)}
                    executionDate={<FormatDateWithLocale date={investmentData?.CreatedDate?.split("_")[0]} />}
                    perSharePrice={investmentData?.SharePrice}
                    btnBlockClassName="mt-4"
                    orderFlag={userCountry}
                />
            ) : (
                <InvestmentPictureOverview
                    image={image}
                    title={title?.toString()}
                    location={location}
                    shares={shares}
                    amount={amount}
                    createdDate={createdDate}
                    price={price}
                />
            )}
            {investmentData?.StatusInvestment === INVESTMENT_STATUS.CANCELED ? (
                <>
                    <h4>{formatMessage(messages.completedInvestment)}</h4>
                    <InvestmentStep className="uncompleted mb-10" title={formatMessage(messages.canceledText)} icon="canceled" />
                </>
            ) : (
                <RenderInvestmentStatus
                    open={open}
                    currentKYCStatus={currentKYCStatus}
                    setKycModalFlag={setKycModalFlag}
                    investmentData={investmentData}
                    hideInvestmentStatusModal={hideInvestmentStatusModal}
                    isKYCCompleted={isKYCCompleted}
                    project={project}
                    isMangopayActive={isMangopayActive}
                    payInWalletBalance={payInWalletBalance}
                    onCloseButtonClick={onCloseButtonClick}
                    fetchPayInWalletBalanceSuccess={fetchPayInWalletBalanceSuccess}
                    fetchDashboardDetailsStart={fetchDashboardDetailsStart}
                    fetchUserPaymentsStart={fetchUserPaymentsStart}
                    userId={userId}
                    isKYCServerError={isKYCServerError}
                />
            )}
            {onBackButtonClick && (
                <div className="bottom-bar-container bg-border-none">
                    <PrevButton
                        onClick={() => {
                            onBackButtonClick();
                        }}
                    />
                </div>
            )}
        </SideModal>
    );
};

const mapStateToProps = createStructuredSelector({
    isKYCCompleted: selectKYCCompleted,
    isMangopayActive: selectMangopayActive,
    payInWalletBalance: selectPayInWalletBalance,
    allProjects: selectProjectDetails,
    userId: selectUserId,
    currentKYCStatus: selectCurrenKYCStatus,
    isKYCServerError: selectKYCServerError,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPayInWalletBalanceSuccess: (data) => dispatch(fetchPayInWalletBalanceSuccess(data)),
    fetchDashboardDetailsStart: () => dispatch(fetchDashboardDetailsStart()),
    fetchUserPaymentsStart: (userId) => dispatch(fetchUserPaymentsStart(userId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvestmentStatusModal);
